import React, { useState } from 'react';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { timestampToDate } from '../../../util/dates';
import { propTypes } from '../../../util/types';
import { getAdditionalFeeOptions, formatMoney } from '../../../util/currency';
import { BOOKING_PROCESS_NAME } from '../../../transactions/transaction';

import {
  Form,
  H6,
  PrimaryButton,
  FieldSelect,
  FieldCheckboxGroupWithQuantity,
  FieldCheckbox
} from '../../../components';
import { types as sdkTypes } from '../../../util/sdkLoader';
import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';
import FieldDateAndTimeInput from './FieldDateAndTimeInput';

import FetchLineItemsError from '../FetchLineItemsError/FetchLineItemsError.js';

import css from './BookingTimeForm.module.css';

const { Money } = sdkTypes;

// When the values of the form are updated we need to fetch
// lineItems from this template's backend for the EstimatedTransactionMaybe
// In case you add more fields to the form, make sure you add
// the values here to the orderData object.
const handleFetchLineItems = props => formValues => {
  const {
    listingId,
    isOwnListing,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    seatsEnabled,
    availabilityType,
    showSpecialPricePopup,
    providerId
  } = props;  
  
  const { bookingStartTime, bookingEndTime, seats, priceVariantName, driverFee, addonsFeeGroup } = formValues.values;
  const startDate = bookingStartTime ? timestampToDate(bookingStartTime) : null;
  const endDate = bookingEndTime ? timestampToDate(bookingEndTime) : null;

  const filteredAddonsFeeGroup = addonsFeeGroup?.filter(o => !isEmpty(o.option));

  // Note: we expect values bookingStartTime and bookingEndTime to be strings
  // which is the default case when the value has been selected through the form
  const isStartBeforeEnd = bookingStartTime < bookingEndTime;
  const seatsMaybe = seatsEnabled && seats > 0 ? { seats: parseInt(seats, 10) } : {};

  const priceVariantMaybe = priceVariantName ? { priceVariantName } : {};

  if (bookingStartTime && bookingEndTime && isStartBeforeEnd && !fetchLineItemsInProgress && !showSpecialPricePopup) {
    const orderData = {
      bookingStart: startDate,
      bookingEnd: endDate,
      ...seatsMaybe,
      ...priceVariantMaybe,
      availabilityType,
      driverFee,
      ...(addonsFeeGroup &&
        filteredAddonsFeeGroup?.length > 0 && { addonsFeeGroup: filteredAddonsFeeGroup }),
    };
    onFetchTransactionLineItems({
      orderData,
      listingId,
      providerId,
      isOwnListing,
    });
  }
};

const onPriceVariantChange = props => value => {
  const { form: formApi, seatsEnabled } = props;

  formApi.batch(() => {
    formApi.change('bookingStartDate', null);
    formApi.change('bookingStartTime', null);
    formApi.change('bookingEndTime', null);
    if (seatsEnabled) {
      formApi.change('seats', 1);
    }
  });
};

const handleOnChange = (formValues, props) => {
  const {
    bookingStartTime,
    bookingEndTime,
    addonsFeeGroup,
    quantity,
    priceVariantName,
    driverFee
  } = formValues.values;

  const startDate = bookingStartTime ? timestampToDate(bookingStartTime) : null;
  const endDate = bookingEndTime ? timestampToDate(bookingEndTime) : null;

  const listingId = props.listingId;
  const providerId = props.providerId;
  const isOwnListing = props.isOwnListing;
  const showSpecialPricePopup = props.showSpecialPricePopup;

  // Note: we expect values bookingStartTime and bookingEndTime to be strings
  // which is the default case when the value has been selected through the form
  const isStartBeforeEnd = bookingStartTime < bookingEndTime;

  const priceVariantMaybe = priceVariantName ? { priceVariantName } : {};
  const filteredAddonsFeeGroup = addonsFeeGroup?.filter(o => !isEmpty(o.option));


  if (
    bookingStartTime &&
    bookingEndTime &&
    isStartBeforeEnd &&
    !props.fetchLineItemsInProgress &&
    !showSpecialPricePopup
  ) {
    props.onFetchTransactionLineItems({
      orderData: {
        bookingStart: startDate,
        bookingEnd: endDate,
        ...(addonsFeeGroup &&
          filteredAddonsFeeGroup?.length > 0 && { addonsFeeGroup: filteredAddonsFeeGroup }),
        quantity: quantity || 1,
        availabilityType: "hour",
        driverFee
      },
      listingId,
      providerId,
      isOwnListing,
    });
  }
}

/**
 * A form for selecting booking time.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {propTypes.money} props.price - The unit price of the listing
 * @param {boolean} props.isOwnListing - Whether the listing is owned by the current user
 * @param {propTypes.uuid} props.listingId - The ID of the listing
 * @param {Object} props.monthlyTimeSlots - The monthly time slots
 * @param {Function} props.onFetchTimeSlots - The function to fetch the time slots
 * @param {string} props.timeZone - The time zone of the listing (e.g. "America/New_York")
 * @param {Function} props.onFetchTransactionLineItems - The function to fetch the transaction line items
 * @param {Object} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether line items are being fetched
 * @param {propTypes.error} props.fetchLineItemsError - The error for fetching line items
 * @param {string} [props.startDatePlaceholder] - The placeholder text for the start date
 * @param {string} [props.endDatePlaceholder] - The placeholder text for the end date
 * @param {number} props.dayCountAvailableForBooking - Number of days available for booking
 * @param {string} props.marketplaceName - Name of the marketplace
 * @param {Array<Object>} [props.priceVariants] - The price variants
 * @param {ReactNode} [props.priceVariantFieldComponent] - The component to use for the price variant field
 * @param {boolean} props.isPublishedListing - Whether the listing is published
 * @returns {JSX.Element}
 */
export const BookingTimeForm = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    price: unitPrice,
    dayCountAvailableForBooking,
    marketplaceName,
    seatsEnabled,
    isPriceVariationsInUse,
    priceVariants = [],
    priceVariantFieldComponent: PriceVariantFieldComponent,
    preselectedPriceVariant,
    isPublishedListing,
    minRentalHours,
    currentDuration,
    setCurrentTx,
    customerTotal,
    pricePerDayAmount,
    publicData,
    marketplaceCurrency,
    deliveryRadiusKm,
    providerId,
    ...rest
  } = props;

  const [seatsOptions, setSeatsOptions] = useState([1]);
  const initialValuesMaybe =
    priceVariants.length > 1 && preselectedPriceVariant
      ? { initialValues: { priceVariantName: preselectedPriceVariant?.name } }
      : priceVariants.length === 1
        ? { initialValues: { priceVariantName: priceVariants?.[0]?.name } }
        : {};

  const classes = classNames(rootClassName || css.root, className);

  return (
    <FinalForm
      {...initialValuesMaybe}
      {...rest}
      mutators={{ ...arrayMutators }}
      unitPrice={unitPrice}
      render={formRenderProps => {
        const {
          endDatePlaceholder,
          startDatePlaceholder,
          form,
          pristine,
          handleSubmit,
          isOwnListing,
          listingId,
          values,
          monthlyTimeSlots,
          timeSlotsForDate,
          onFetchTimeSlots,
          timeZone,
          lineItems,
          fetchLineItemsInProgress,
          fetchLineItemsError,
          payoutDetailsWarning,
          listing,
        } = formRenderProps;

        const startTime = values?.bookingStartTime ? values.bookingStartTime : null;
        const endTime = values?.bookingEndTime ? values.bookingEndTime : null;
        const startDate = startTime ? timestampToDate(startTime) : null;
        const endDate = endTime ? timestampToDate(endTime) : null;
        const priceVariantName = values?.priceVariantName || null;

        // This is the place to collect breakdown estimation data. See the
        // EstimatedCustomerBreakdownMaybe component to change the calculations
        // for customized payment processes.
        const breakdownData =
          startDate && endDate
            ? {
              startDate,
              endDate,
            }
            : null;

        const showEstimatedBreakdown =
          breakdownData && lineItems && !fetchLineItemsError;

        const invalidDuration = currentDuration < parseInt(minRentalHours);

        const onHandleFetchLineItems = handleFetchLineItems(props);
        const submitDisabled = (isPriceVariationsInUse && !isPublishedListing) || (!!values?.bookingStartTime && invalidDuration);

        const driverAvailable = publicData?.driverAvailable?.[0] === "yes" && !publicData?.driverIncludedInPrice;
        const driverPricePerHour = publicData?.driverPricePerHour;
        const driverPrice = !!driverPricePerHour ? formatMoney(intl, new Money(driverPricePerHour, unitPrice.currency)) : null
        const showDriverFee = driverAvailable && startTime && endTime;

        const addons = publicData?.addons;
        const addonsFeeParams = { intl, valuesArray: addons, currency: marketplaceCurrency };
        const addonsFee = addons && getAdditionalFeeOptions(addonsFeeParams, "hour");
        const showAddonsFee = !!addons?.length && startTime && endTime;

        const showDailyTip = customerTotal > pricePerDayAmount && !fetchLineItemsInProgress;

        return (
          <Form onSubmit={handleSubmit} className={classes} enforcePagePreloadFor="CheckoutPage">
            <FormSpy subscription={{ values: true }} onChange={values => { handleOnChange(values, props) }} />
            {PriceVariantFieldComponent ? (
              <PriceVariantFieldComponent
                priceVariants={priceVariants}
                priceVariantName={priceVariantName}
                onPriceVariantChange={onPriceVariantChange(formRenderProps)}
                disabled={!isPublishedListing}
              />
            ) : null}

            {monthlyTimeSlots && timeZone ? (
              <FieldDateAndTimeInput
                seatsEnabled={seatsEnabled}
                setSeatsOptions={setSeatsOptions}
                startDateInputProps={{
                  label: intl.formatMessage({ id: 'BookingTimeForm.bookingStartTitle' }),
                  placeholderText: startDatePlaceholder,
                }}
                endDateInputProps={{
                  label: intl.formatMessage({ id: 'BookingTimeForm.bookingEndTitle' }),
                  placeholderText: endDatePlaceholder,
                }}
                className={css.bookingDates}
                listingId={listingId}
                onFetchTimeSlots={onFetchTimeSlots}
                monthlyTimeSlots={monthlyTimeSlots}
                timeSlotsForDate={timeSlotsForDate}
                values={values}
                intl={intl}
                form={form}
                pristine={pristine}
                disabled={isPriceVariationsInUse && !priceVariantName}
                timeZone={timeZone}
                dayCountAvailableForBooking={dayCountAvailableForBooking}
                handleFetchLineItems={onHandleFetchLineItems}
                listing={listing}
              />
            ) : null}
            <p className={classNames(css.finePrint, css.minDuration, { [css.minDurationError]: endTime && invalidDuration && !fetchLineItemsInProgress })}>
              {intl.formatMessage({ id: 'BookingTimeForm.minRentalHours' }, { minRentalHours })}
            </p>
            {endTime && currentDuration && (
              <p className={classNames(css.finePrint, css.minDuration)}>
                {intl.formatMessage({ id: 'BookingTimeForm.currentDuration' }, { currentDuration })}
              </p>
            )}
            {seatsEnabled ? (
              <FieldSelect
                name="seats"
                id="seats"
                disabled={!startTime}
                showLabelAsDisabled={!startTime}
                label={intl.formatMessage({ id: 'BookingTimeForm.seatsTitle' })}
                className={css.fieldSeats}
                onChange={values => {
                  onHandleFetchLineItems({
                    values: {
                      priceVariantName,
                      bookingStartDate: startDate,
                      bookingStartTime: startTime,
                      bookingEndDate: endDate,
                      bookingEndTime: endTime,
                      seats: values,
                    },
                  });
                }}
              >
                <option disabled value="">
                  {intl.formatMessage({ id: 'BookingTimeForm.seatsPlaceholder' })}
                </option>
                {seatsOptions.map(s => (
                  <option value={s} key={s}>
                    {s}
                  </option>
                ))}
              </FieldSelect>
            ) : null}

            {(showDriverFee || showAddonsFee) && (
              <div className={css.bookingAddons}>
                {showDriverFee && (
                  <div className={css.driverFee}>
                    <FieldCheckbox
                      id="driverFee"
                      name="driverFee"
                      label={intl.formatMessage(
                        { id: 'BookingTimeForm.driverFee' },
                        { 
                          label: <span>{intl.formatMessage({ id: 'BookingTimeForm.driverLabel' })}</span>,
                          price: <span>({driverPrice} {intl.formatMessage({ id: 'BookingTimeForm.driverFeePerHour' })})</span> 
                        }
                      )}
                      value="yes"
                    />
                  </div>
                )}

                {showAddonsFee &&
                  <div>
                    <FieldCheckboxGroupWithQuantity
                      id={`addonsFeeGroup`}
                      name='addonsFeeGroup'
                      options={addonsFee}
                      arrayName={'addonsFeeGroup'}
                      optionLabelClassName={css.addonText}
                      className={css.bookingCheckboxGroup}
                      quantityClassName={css.quantityInput}
                    />
                  </div>
                }
              </div>
            )}
            {showEstimatedBreakdown ? (
              <div className={css.priceBreakdownContainer}>
                <H6 as="h3" className={css.bookingBreakdownTitle}>
                  <FormattedMessage id="BookingTimeForm.priceBreakdownTitle" />
                </H6>
                <hr className={css.totalDivider} />
                <EstimatedCustomerBreakdownMaybe
                  breakdownData={breakdownData}
                  lineItems={lineItems}
                  timeZone={timeZone}
                  currency={unitPrice.currency}
                  marketplaceName={marketplaceName}
                  processName={BOOKING_PROCESS_NAME}
                  setCurrentTx={setCurrentTx}
                />
              </div>
            ) : null}
            <FetchLineItemsError error={fetchLineItemsError} />

            <div className={css.submitButton}>
              {deliveryRadiusKm && (
                <p className={css.finePrint}>
                  {intl.formatMessage({ id: 'BookingTimeForm.deliveryRadiusKm' }, { deliveryRadiusKm })}
                </p>
              )}
              {showDailyTip && (
                <p className={classNames(css.finePrint, css.finePrintAttention)}>
                  <FormattedMessage id="BookingTimeForm.moreProfitableTip" />
                </p>
              )}
              <PrimaryButton
                type="submit"
                inProgress={fetchLineItemsInProgress}
                disabled={submitDisabled}
              >
                <FormattedMessage id="BookingTimeForm.requestToBook" />
              </PrimaryButton>
            </div>

            <p className={css.finePrint}>
              {payoutDetailsWarning ? (
                payoutDetailsWarning
              ) : (
                <FormattedMessage
                  id={
                    isOwnListing
                      ? 'BookingTimeForm.ownListing'
                      : 'BookingTimeForm.youWontBeChargedInfo'
                  }
                />
              )}
            </p>
          </Form>
        );
      }}
    />
  );
};

export default BookingTimeForm;
